import { useState } from 'react'
import { X, Upload, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { cn } from '../../../lib/utils'
import {
  borderPrimary,
  semiBoldText,
  primaryText,
  largeTextSize,
  smallTextSize,
  secondaryText,
  whiteBG,
  baseTextSize,
  mediumText,
} from '../../../styling/stylingHelper'
import FileDropzone from '../../ui/FileDropzone'
import { ProductAggregation } from '../../orderhistory/types'
import { compareSupplier, SupplierComparisonResult } from '../../../services/sourcing/supplierComparisonService'
import { buildCompareItems } from './supplierCompareShared'

interface CompareSupplierModalProps {
  products: ProductAggregation[]
  restaurantId: string
  onClose: () => void
  onComplete: (result: SupplierComparisonResult) => void
}

/**
 * Modal that collects the new supplier's name + price file and runs the comparison
 * against the period's products. On success it hands the result up so the page can
 * switch to the results view — the upload itself never leaves the products page.
 */
const CompareSupplierModal = ({ products, restaurantId, onClose, onComplete }: CompareSupplierModalProps) => {
  const { t } = useTranslation()

  const [supplierName, setSupplierName] = useState('')
  const [files, setFiles] = useState<File[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleCompare = async (e: React.FormEvent) => {
    e.preventDefault()
    if (files.length === 0) {
      setError(t('products.compareSupplier.filesRequired'))
      return
    }
    setIsLoading(true)
    setError(null)
    try {
      const items = buildCompareItems(products)
      const result = await compareSupplier(files, items, restaurantId, supplierName.trim())
      if (result.extractedCount === 0) {
        setError(t('products.compareSupplier.noProductsInFile'))
        return
      }
      onComplete(result)
    } catch (err) {
      console.error('[SUPPLIER_COMPARE] Failed to compare:', err)
      setError(t('products.compareSupplier.failed'))
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 dark:bg-black/50 flex items-center justify-center z-[60] p-4">
      <div className={cn('bg-white dark:bg-background-dark rounded-xl shadow-xl w-full max-w-md border flex flex-col max-h-[90vh]', borderPrimary)}>
        <div className="p-4 border-b border-borderPrimary dark:border-borderPrimary-dark flex justify-between items-start">
          <h2 className={cn(semiBoldText, largeTextSize, primaryText)}>
            {t('products.compareSupplier.title')}
          </h2>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 disabled:opacity-50"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-4 overflow-y-auto">
          <p className={cn(secondaryText, smallTextSize, 'mb-4')}>
            {t('products.compareSupplier.uploadHint', { count: products.length })}
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 rounded-lg text-red-800 dark:text-red-200 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleCompare} className="space-y-4">
            <div>
              <label className={cn(mediumText, baseTextSize, secondaryText, 'mb-2 block')}>
                {t('products.compareSupplier.selectFile')} *
              </label>
              <FileDropzone
                files={files}
                onFilesChange={setFiles}
                accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,image/*"
                multiple
                disabled={isLoading}
                hint={t('products.compareSupplier.selectFileHint')}
              />
            </div>

            <div>
              <label className={cn(mediumText, baseTextSize, secondaryText, 'mb-2 block')}>
                {t('products.compareSupplier.supplierName')}
              </label>
              <input
                type="text"
                value={supplierName}
                onChange={(e) => setSupplierName(e.target.value)}
                placeholder={t('products.compareSupplier.supplierNamePlaceholder')}
                disabled={isLoading}
                className={cn('w-full px-4 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-gray-900 dark:focus:ring-gray-400 disabled:opacity-50', borderPrimary, whiteBG, primaryText)}
              />
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                disabled={isLoading}
                className={cn(smallTextSize, secondaryText, semiBoldText, 'rounded-xl px-4 py-3', whiteBG, 'border', borderPrimary, 'hover:bg-gray-50 dark:hover:bg-backgroundDarker-dark', 'flex-1', 'disabled:opacity-50')}
              >
                {t('products.cancel')}
              </button>
              <button
                type="submit"
                disabled={isLoading}
                className={cn(smallTextSize, 'text-white dark:text-white', semiBoldText, 'bg-darkGreenColor dark:bg-darkGreenColor-dark rounded-xl px-4 py-3', 'hover:bg-darkGreenColor/95', 'flex-1 flex items-center justify-center gap-2', 'disabled:opacity-50 disabled:cursor-not-allowed')}
              >
                {isLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {isLoading ? t('products.compareSupplier.comparing') : t('products.compareSupplier.compare')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CompareSupplierModal
